import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

function ContactDetails() {
  const { contactId } = useParams(); // Get contact ID from URL
  const [contact, setContact] = useState(null);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [contactedByPhone, setContactedByPhone] = useState(false);
  const [contactedByEmail, setContactedByEmail] = useState(false);
  const [showModal, setShowModal] = useState(false); // For delete modal
  const navigate = useNavigate();

  const token = localStorage.getItem('access_token');  // JWT token

  // Fetch contact details by ID
  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await fetch(`/api/admin/contact/${contactId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,  // Include JWT in request headers
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        if (response.ok) {
          setContact(data);
          setContactedByPhone(data.contactedByPhone);
          setContactedByEmail(data.contactedByEmail);
        } else {
          setMessage('Contact not found');
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
        setMessage('Error fetching contact details.');
      }
    };

    fetchContact();
  }, [contactId, token]);

  // Handle checkbox change for "Contacted by Phone" and "Contacted by Email"
  const handleContactStatusChange = async (type, value) => {
    // Update the specific checkbox state
    if (type === 'contactedByPhone') {
      setContactedByPhone(value);
    } else if (type === 'contactedByEmail') {
      setContactedByEmail(value);
    }

    try {
      // Always send both fields to the backend to ensure both statuses are saved
      const response = await fetch(`/api/admin/contact/${contactId}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,  // Include JWT in request headers
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactedByPhone: type === 'contactedByPhone' ? value : contactedByPhone,
          contactedByEmail: type === 'contactedByEmail' ? value : contactedByEmail,
        }),
      });

      if (response.ok) {
        setMessage(`${type === 'contactedByPhone' ? 'Phone' : 'Email'} contact status updated!`);
      } else {
        setMessage('Failed to update contact status.');
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  // Handle note addition
  const handleAddNote = async () => {
    if (!note) return;
    try {
      const response = await fetch(`/api/admin/contact/${contactId}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,  // Include JWT in request headers
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note,
          contactedByPhone,
          contactedByEmail,
        }),
      });

      if (response.ok) {
        setMessage('Note added successfully!');
        setContact((prevContact) => ({
          ...prevContact,
          notes: [...prevContact.notes, { content: note, timestamp: new Date().toISOString() }],
        }));
        setNote(''); // Clear the note input
      } else {
        setMessage('Failed to add note.');
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  // Handle note deletion
  const handleDeleteNote = async (noteIndex) => {
    try {
      const response = await fetch(`/api/admin/contact/${contactId}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,  // Include JWT in request headers
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          noteIndex,
        }),
      });

      if (response.ok) {
        setMessage('Note deleted successfully!');
        setContact((prevContact) => ({
          ...prevContact,
          notes: prevContact.notes.filter((_, index) => index !== noteIndex),
        }));
      } else {
        setMessage('Failed to delete note.');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Handle contact deletion
  const handleDeleteContact = async () => {
    try {
      const response = await fetch(`/api/admin/contact/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,  // Include JWT in request headers
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contact_id: contactId }),
      });

      if (response.ok) {
        setMessage('Contact deleted successfully!');
        navigate('/dashboard'); // Redirect after deletion
      } else {
        setMessage('Failed to delete contact.');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  // Open/Close delete confirmation modal
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  if (!contact) return <p>Loading...</p>;

  return (
    <div className="container mx-auto py-20">
      <h1 className="text-3xl font-bold mb-6">Contact Details</h1>
      {message && <p className="text-red-500 mb-4">{message}</p>}
      <p>
        <strong>Address:</strong> {contact.streetAddress}, {contact.city}, {contact.state} {contact.zip}
      </p>
      <p>
        <strong>Email:</strong> {contact.email}
      </p>
      <p>
        <strong>Phone:</strong> {contact.phone}
      </p>

      {/* Contacted status checkboxes */}
      <div className="mt-4">
        <label className="mr-2">
          <input
            type="checkbox"
            checked={contactedByPhone}
            onChange={(e) => handleContactStatusChange('contactedByPhone', e.target.checked)}
          />
          Contacted by Phone
        </label>

        <label className="ml-4">
          <input
            type="checkbox"
            checked={contactedByEmail}
            onChange={(e) => handleContactStatusChange('contactedByEmail', e.target.checked)}
          />
          Contacted by Email
        </label>
      </div>

      {/* Add note input */}
      <div className="mt-4">
        <textarea
          className="w-full p-2 border border-gray-300 rounded"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (max 500 characters)"
          maxLength={500}
        />
      </div>
      <button
        onClick={handleAddNote}
        className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-500"
      >
        Add Note
      </button>

      {/* Notes section */}
      <div className="mt-6">
        <h3 className="text-lg font-bold">Notes:</h3>
        <ul>
          {contact.notes.map((note, index) => (
            <li key={index} className="mb-2 flex justify-between">
              <p>{note.content} <span className="text-gray-500 text-sm">({new Date(note.timestamp).toLocaleString()})</span></p>
              <button
                onClick={() => handleDeleteNote(index)}
                className="text-red-600 hover:text-red-800"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Delete contact button */}
      <button
        onClick={openModal}
        className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500"
      >
        Delete Contact
      </button>

      {/* Confirmation modal for deleting contact */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded shadow-md">
            <h2 className="text-lg font-bold mb-4">Are you sure you want to delete this contact?</h2>
            <div className="flex justify-end">
              <button
                onClick={handleDeleteContact}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500 mr-2"
              >
                Confirm
              </button>
              <button
                onClick={closeModal}
                className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactDetails;

